(() => {
  'use strict';

  const widgets = Array.from(document.querySelectorAll('[data-cap-sr1-advanced]'));
  if (!widgets.length) return;

  const SUPABASE_URL = 'https://vumyshrwjivysgplsgrf.supabase.co';
  const SUPABASE_KEY = 'sb_publishable_wDS4N9-_DV-SAFTaSsgTPg_kRDTuYYk';
  const RESOLVER_RPC = 'rpc_storefront_rep_identifier_search_v2';
  const FIND_PARTS_URL = '/pages/find-parts';
  const candidateContexts = new WeakMap();
  const widgetState = new WeakMap();
  const clean = (value) => String(value == null ? '' : value).replace(/\s+/g, ' ').trim();

  const delay = (milliseconds, signal) => new Promise((resolve, reject) => {
    const timeout = window.setTimeout(resolve, milliseconds);
    signal?.addEventListener('abort', () => {
      window.clearTimeout(timeout);
      reject(new DOMException('Aborted', 'AbortError'));
    }, { once: true });
  });

  function simplifyWidgetCopy(widget) {
    const toggleLabel = widget.querySelector('[data-cap-sr1-toggle] span');
    if (toggleLabel) {
      const strong = toggleLabel.querySelector('strong') || document.createElement('strong');
      strong.textContent = 'Advanced Search';
      toggleLabel.replaceChildren(strong);
    }
    widget.querySelector('.cap-sr1-advanced__copy')?.remove();
  }

  function rootFor(widget) {
    return widget.closest('#cap-home-fitment-search, #cap-find-parts-app');
  }

  function setStatus(widget, message) {
    const status = widget.querySelector('[data-cap-sr1-status]');
    if (status) status.textContent = message || '';
  }

  function clearResults(widget) {
    const results = widget.querySelector('[data-cap-sr1-results]');
    if (!results) return;
    results.replaceChildren();
    results.hidden = true;
  }

  function cancelLookup(widget) {
    const current = widgetState.get(widget);
    current?.controller.abort();
    widgetState.delete(widget);
  }

  function productUrl(candidate) {
    const year = clean(candidate?.year);
    const make = clean(candidate?.make);
    const model = clean(candidate?.model);
    const collectionGid = clean(candidate?.shopify_collection_gid);
    const productGid = clean(candidate?.shopify_product_gid);
    if (!year || !make || !model || !productGid) return '';

    const url = new URL(FIND_PARTS_URL, window.location.origin);
    url.searchParams.set('mode', 'parts');
    url.searchParams.set('year', year);
    url.searchParams.set('make', make);
    url.searchParams.set('model', model);
    if (collectionGid) url.searchParams.set('vehicle_collection', collectionGid);
    url.searchParams.set('product_gid', productGid);
    return `${url.pathname}?${url.searchParams.toString()}`;
  }

  function productType(candidate) {
    return clean(candidate?.part_subcategory || candidate?.part_name || candidate?.product_title || 'Inventory Product');
  }

  function openCandidate(widget, candidate) {
    const url = productUrl(candidate);
    if (!url) {
      setStatus(widget, 'This match is missing product identity. Try another identifier.');
      return;
    }
    setStatus(widget, `Opening ${productType(candidate)}...`);
    window.location.assign(url);
  }

  function candidateLabel(candidate) {
    const vehicle = [candidate?.year, candidate?.make, candidate?.model].map(clean).filter(Boolean).join(' ') || 'Matching inventory';
    return `${vehicle} — ${productType(candidate)}`;
  }

  function renderCandidates(widget, rows) {
    const candidates = (Array.isArray(rows) ? rows : []).filter(row => productUrl(row));
    if (!candidates.length) {
      clearResults(widget);
      setStatus(widget, 'No inventory product matched that Stock # or VIN #.');
      return;
    }
    if (candidates.length === 1) {
      openCandidate(widget, candidates[0]);
      return;
    }

    const results = widget.querySelector('[data-cap-sr1-results]');
    if (!results) return;
    results.replaceChildren();
    candidates.forEach((candidate) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cap-sr1-advanced__result';
      button.setAttribute('role', 'option');
      const title = document.createElement('strong');
      title.textContent = candidateLabel(candidate);
      const match = document.createElement('small');
      const matchedBy = clean(candidate?.matched_by) === 'vin' ? 'VIN' : 'Stock';
      match.textContent = `${matchedBy} match · ${clean(candidate?.yard) || 'Callahan'}`;
      button.append(title, match);
      candidateContexts.set(button, candidate);
      results.appendChild(button);
    });
    results.hidden = false;
    setStatus(widget, `${candidates.length} product matches found. Choose the exact product.`);
  }

  async function requestCandidates(query, signal) {
    const retryableStatuses = new Set([408, 425, 429, 500, 502, 503, 504]);
    let lastError;

    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const response = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${RESOLVER_RPC}`, {
          method: 'POST',
          headers: {
            apikey: SUPABASE_KEY,
            Authorization: `Bearer ${SUPABASE_KEY}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ p_query: query }),
          signal
        });
        if (response.ok) {
          const rows = await response.json();
          if (!Array.isArray(rows)) throw new TypeError('Advanced search returned an invalid response.');
          return rows;
        }
        const error = new Error(`Advanced search failed: ${response.status}`);
        error.retryable = retryableStatuses.has(response.status);
        throw error;
      } catch (error) {
        if (error?.name === 'AbortError') throw error;
        lastError = error;
        if (attempt === 1 || error?.retryable === false) break;
        await delay(250, signal);
      }
    }

    throw lastError || new Error('Advanced search failed.');
  }

  async function searchIdentifier(widget) {
    const input = widget.querySelector('[data-cap-sr1-input]');
    const submit = widget.querySelector('[data-cap-sr1-submit]');
    const query = clean(input?.value);
    if (query.length < 2) {
      setStatus(widget, 'Enter a Stock # or full VIN #.');
      input?.focus();
      return;
    }

    cancelLookup(widget);
    clearResults(widget);
    const controller = new AbortController();
    widgetState.set(widget, { controller });
    if (submit) submit.disabled = true;
    if (input) input.setAttribute('aria-busy', 'true');
    setStatus(widget, 'Searching inventory products...');

    try {
      renderCandidates(widget, await requestCandidates(query, controller.signal));
    } catch (error) {
      if (error?.name !== 'AbortError') setStatus(widget, 'Advanced Search is temporarily unavailable. Please retry.');
    } finally {
      if (widgetState.get(widget)?.controller === controller) {
        widgetState.delete(widget);
        if (submit) submit.disabled = false;
        if (input) input.removeAttribute('aria-busy');
      }
    }
  }

  function bindWidget(widget) {
    const toggle = widget.querySelector('[data-cap-sr1-toggle]');
    const panel = widget.querySelector('[data-cap-sr1-panel]');
    const form = widget.querySelector('[data-cap-sr1-form]');
    const input = widget.querySelector('[data-cap-sr1-input]');
    const results = widget.querySelector('[data-cap-sr1-results]');
    const root = rootFor(widget);

    simplifyWidgetCopy(widget);

    toggle?.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (panel) panel.hidden = !open;
      if (open) input?.focus({ preventScroll: true });
      else cancelLookup(widget);
    });

    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      void searchIdentifier(widget);
    });

    input?.addEventListener('input', () => {
      cancelLookup(widget);
      clearResults(widget);
      setStatus(widget, '');
    });

    results?.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const button = target?.closest('.cap-sr1-advanced__result');
      if (!button || !results.contains(button)) return;
      openCandidate(widget, candidateContexts.get(button));
    });

    root?.querySelector('.cap-home-fitment__form, .cap-find-parts__filters')?.addEventListener('submit', () => cancelLookup(widget));
    root?.querySelector('.cap-home-fitment__reset, .cap-find-parts__clear')?.addEventListener('click', () => {
      cancelLookup(widget);
      clearResults(widget);
      if (input) input.value = '';
      setStatus(widget, '');
    });
  }

  async function resolveVisibility() {
    let isSalesRep = widgets.some(widget => clean(rootFor(widget)?.dataset.salesRep).toLowerCase() === 'true');
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error(`Cart lookup failed: ${response.status}`);
      const cart = await response.json();
      const cartSalesRep = clean(cart?.attributes?.sales_rep).toLowerCase() === 'true';
      isSalesRep = isSalesRep || cartSalesRep;
    } catch (error) {
      // Preserve server-rendered state if cart lookup is unavailable.
    }

    widgets.forEach((widget) => {
      widget.hidden = !isSalesRep;
      if (!isSalesRep) cancelLookup(widget);
    });
  }

  widgets.forEach(bindWidget);
  void resolveVisibility();
})();